/* eslint-disable */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next'

import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';

// constants
import { MAX_ALLOWED_GROUPS } from './groupsTable.constants';

// components
import LoadingSpinner from '../../../../../loadingSpinner'

// helpers
import {
  isGroupSelected,
  shouldDisableGroup,
  shouldDisableSelectAll,
  areAllGroupsChecked,
  createGroup,
  getGroupIds,
  getSelectedGroupsWithOutSelectedGroupId,
  sortGroups,
} from './groupsTable.helpers';
import { isSuperAdmin } from '../../../../../../helpers/user';

const GroupsTable = ({
  groups,
  selectedGroups,
  setSelectedGroups,
  areGroupsLoading,
  getSelectedGroupsCount,
  displayAlert,
}) => {
  const isSuperAdminUser = isSuperAdmin();
  const [sortBy, setSortBy] = useState('displayName');
  const [sortOrder, setSortOrder] = useState('asc');
  const { t } = useTranslation()

  const handleCheckboxChange = (group) => {
    let updatedSelectedGroups = [];
    const selectedGroupId = group?.id;
    if (isGroupSelected(selectedGroupId, selectedGroups)) {
      updatedSelectedGroups = getSelectedGroupsWithOutSelectedGroupId(selectedGroups, selectedGroupId)
    } else {
      updatedSelectedGroups = [...selectedGroups, createGroup(group)];
    }
    const selectedGroupsCount = getSelectedGroupsCount(updatedSelectedGroups)
    if (selectedGroupsCount >= MAX_ALLOWED_GROUPS) {
      displayAlert(true)
    } else {
      displayAlert(false)
    }
    setSelectedGroups(updatedSelectedGroups);
  };

  const handleSelectAllCheckBoxChange = () => {
    const allGroupsChecked = areAllGroupsChecked(groups, selectedGroups);
    let updatedSelectedGroups = [];
    const groupIds = getGroupIds(groups);
    if (allGroupsChecked) {
      updatedSelectedGroups = selectedGroups.filter(
        (selectedGroup) => !groupIds.includes(selectedGroup?.value)
      );
    } else {
      updatedSelectedGroups = [...selectedGroups];
      groups.forEach((group) => {
        if (!isGroupSelected(group?.id, selectedGroups)) {
          updatedSelectedGroups.push(createGroup(group));
        }
      });
    }
    const selectedGroupsCount = getSelectedGroupsCount(updatedSelectedGroups)
    if (selectedGroupsCount >= MAX_ALLOWED_GROUPS) {
      displayAlert(true)
    } else {
      displayAlert(false)
    }
    setSelectedGroups(updatedSelectedGroups);
  };

  const handleSort = (property) => {
    const newOrder = sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(property);
    setSortOrder(newOrder);
  };

  const allGroupsChecked = areAllGroupsChecked(groups, selectedGroups);

  const sortedGroups = useMemo(() => sortGroups({ groups, sortOrder, sortBy }), [groups, sortOrder, sortBy])

  const renderTable = () => (
    <Table stickyHeader sx={{ marginLeft: '12px', marginRight: '12px', marginBottom: '11px', maxWidth: '725px' }}>
      <TableHead>
        <TableRow sx={{
          zIndex: 1000, '& .MuiTableCell-root': {
            height: '42px',
            padding: '0',
            backgroundColor: '#f9f8f7'
          },
        }}>
          <TableCell sx={{ fontFamily: 'Johnson Display', color: '#00000', fontWeight: 'bold', fontSize: '14px', borderRight: '1px solid #C1BBB3', borderBottom: '1px solid #C1BBB3', paddingLeft: '10px !important', width: '16%' }}>
            <FormControlLabel
              control={(
                <Checkbox
                  disabled={shouldDisableSelectAll({ groups, isSuperAdminUser, getSelectedGroupsCount, selectedGroups })}
                  sx={{
                    color: shouldDisableSelectAll({ groups, isSuperAdminUser, getSelectedGroupsCount, selectedGroups })
                      ? 'grey'
                      : '#5E5047',
                    '&.Mui-checked': {
                      color: 'black',
                    },
                    padding: '0 !important',
                    margin: '0 !important'
                  }}
                  checked={allGroupsChecked}
                  onChange={handleSelectAllCheckBoxChange}
                />
              )}
              sx={{ padding: '0', margin: '0', paddingRight: '8px' }}
            />
            {t('group-modal-table-heading-select-all')}
          </TableCell>

          <TableCell sx={{ fontFamily: 'Johnson Display', fontSize: '14px', fontWeight: 'bold', borderRight: '1px solid #C1BBB3', borderBottom: '1px solid #C1BBB3', paddingLeft: '13px !important', width: '35%' }}>
            <TableSortLabel
              active={sortBy === 'displayName'}
              direction={sortOrder}
              onClick={() => handleSort('displayName')}
              style={{flexDirection: 'row-reverse'}}
            >
              {t('group-modal-table-heading-group-name')}
            </TableSortLabel>
          </TableCell>
          <TableCell sx={{ fontFamily: 'Johnson Display', fontSize: '14px', fontWeight: 'bold', paddingLeft: '16px !important', borderBottom: '1px solid #C1BBB3', width: '48%' }}>{t('group-modal-table-heading-description')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedGroups.map((group) => (
          <TableRow key={group.id} sx={{
            '& .MuiTableCell-root': {
              height: '54px',
              padding: '0'
            },
          }}>
            <TableCell sx={{ borderRight: '1px solid #C1BBB3', borderBottom: '1px solid #C1BBB3', paddingLeft: '10px !important' }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    disabled={shouldDisableGroup({ group, isSuperAdminUser, getSelectedGroupsCount, selectedGroups })}
                    checked={isGroupSelected(group.id, selectedGroups)}
                    onChange={() => handleCheckboxChange(group)}
                    sx={{
                      color: shouldDisableGroup({ group, isSuperAdminUser, getSelectedGroupsCount, selectedGroups })
                        ? 'grey'
                        : '#5E5047',
                        zIndex: 1,
                      '&.Mui-checked': {
                        color: 'black',
                      },
                      padding: '0 !important',
                      margin: '0 !important'
                    }}
                  />
                )}
                sx={{ padding: '0', margin: '0', paddingRight: '8px' }}
              />
              {t('group-modal-table-row-select')}
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #C1BBB3', borderBottom: '1px solid #C1BBB3', paddingLeft: '13px !important' }}>
              <Chip
                label={group.displayName}
                variant="outlined"
                sx={{
                  fontSize: '16px !important',
                  font: 'Johnson Text !important'
                }}
              />
            </TableCell>
            <TableCell sx={{ fontFamily: 'Johnson Text', paddingLeft: '16px !important', paddingTop: '12px !important', paddingBottom: '8px !important', borderBottom: '1px solid #C1BBB3' }}>
              {group.description}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  if (areGroupsLoading) {
    return (
      <div style={{
				display: 'flex',
        height: '15vw',
				justifyContent: 'center',
				alignItems: 'center',
			}}>
        <LoadingSpinner centered={false} />
      </div>
    )
  }
  return (
    <TableContainer sx={{ height: '100%', maxHeight: '326px', overflowY: 'scroll' }}>{renderTable()}</TableContainer>
  );
};

export default GroupsTable;
